import { useEffect, useRef } from "react";
import { create } from "zustand";
import useUserStore from "@store/useUserStore";
import { getSessionUserInfo } from "api/user";
import { getPublicUserKey } from "utils/userIdentity";

// backfill 시도가 끝났는지(성공/실패 무관)를 화면이 알 수 있게 하는 비영속 상태.
// 세션 스토어(userInfoStorage)에 넣지 않는다 — 저장 스키마를 바꾸지 않기 위해서다.
export const useBackfillSettledStore = create((set) => ({
    settledUserId: null,
    markSettled: (userId) => set({ settledUserId: userId }),
}));

export const IDENTITY_STATUS = {
    ANONYMOUS: "anonymous", // 비로그인
    RESOLVED: "resolved", // public_id 확보
    PENDING: "pending", // backfill 진행 중 — 판정 보류
    UNAVAILABLE: "unavailable", // backfill 이 끝났는데도 public_id 없음 — 판정 불가
};

// 현재 세션의 공개 식별자 확정 상태. pending 을 무한 대기로 만들지 않으려면
// 화면은 UNAVAILABLE 을 종료 상태로 다뤄야 한다.
export const usePublicIdentityStatus = () => {
    const userInfo = useUserStore((state) => state.userInfo);
    const settledUserId = useBackfillSettledStore((state) => state.settledUserId);

    if (!userInfo?.user_id) return IDENTITY_STATUS.ANONYMOUS;
    if (getPublicUserKey(userInfo)) return IDENTITY_STATUS.RESOLVED;
    return settledUserId === userInfo.user_id
        ? IDENTITY_STATUS.UNAVAILABLE
        : IDENTITY_STATUS.PENDING;
};

// persisted 세션(userInfoStorage)에 public_id가 없으면 계정당 1회 재조회해 채운다.
const usePublicIdBackfill = () => {
    const userInfo = useUserStore((state) => state.userInfo);
    const setUserInfo = useUserStore((state) => state.setUserInfo);
    // 시도한 계정을 기억한다. boolean guard 였을 때는 로그아웃 후 다른 계정으로
    // 로그인해도(SPA 전환이라 리로드가 없다) 그 계정은 영영 채워지지 않았다.
    const attemptedUserIdRef = useRef(null);

    useEffect(() => {
        const userId = userInfo?.user_id;
        if (!userId || userInfo.public_id) return;
        if (attemptedUserIdRef.current === userId) return;
        attemptedUserIdRef.current = userId;
        // 이 계정의 과거 종료 기록은 새 시도가 시작되는 순간 낡은 값이 된다.
        // 지우지 않으면 요청 진행 중에도 UNAVAILABLE(종료)로 보인다.
        // 다른 계정의 기록은 건드리지 않는다.
        if (useBackfillSettledStore.getState().settledUserId === userId) {
            useBackfillSettledStore.getState().markSettled(null);
        }
        // 토큰 기반 본인 조회. user-info/{user_id} 는 이메일 user_id 를 받지 않아
        // (int pk/uuid 라우트뿐) 404 였다 — BE#506.
        getSessionUserInfo()
            .then((res) => {
                if (!res?.public_id) return;
                // 토큰의 계정이 이 세션 계정과 다르면 다른 계정의 public_id 를
                // 붙이게 되므로 반영하지 않는다.
                if (res.user_id && res.user_id !== userId) return;
                // 계정이 바뀐 뒤 도착한 이전 계정 응답은 버린다.
                const latest = useUserStore.getState().userInfo;
                if (!latest?.user_id || latest.user_id !== userId) return;
                setUserInfo({ ...latest, public_id: res.public_id });
            })
            .catch(() => {
                // 계정당 1회만 시도한다 — 재시도 루프를 만들지 않는다
            })
            .finally(() => {
                // 성공/실패와 무관하게 "시도는 끝났다"를 알린다.
                // 단, 계정이 이미 바뀌었다면 이 종료 신호는 stale 이다 — 현재 계정의
                // 신호를 덮으면 그 계정이 PENDING 으로 되돌아가 무한 대기에 갇힌다.
                const latest = useUserStore.getState().userInfo;
                if (latest?.user_id !== userId) {
                    // 세션이 떠난 뒤 끝난 시도는 이 계정의 결과를 확정하지 못했다.
                    // 시도 기록을 지워 같은 계정으로 다시 로그인하면 재시도할 수 있게 한다.
                    if (attemptedUserIdRef.current === userId) {
                        attemptedUserIdRef.current = null;
                    }
                    return;
                }
                useBackfillSettledStore.getState().markSettled(userId);
            });
    }, [userInfo, setUserInfo]);
};

export default usePublicIdBackfill;
